import { useMutation } from '@apollo/client';
import type { UseLalSegmentsOperation } from '@openx/components-segment/lib/utils/context/SegmentHookTypes';
import type { SegmentOperationResult } from '@openx/components-segment/types';
import { type LalSegmentBase, type LalSegmentRowActionType, lalSegmentRowActionType } from '@openx/types/segments';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../components/AuthContext/context';
import { CreateLogDocument } from '../../../graphql/mutation/error/createLog.generated';
import { ArchiveLalSegmentDocument } from '../../../graphql/mutation/segments/lal/ArchiveLalSegment.generated';

const finishedActionLabel = {
  [lalSegmentRowActionType.DELETE]: 'archived',
};

export const useLalSegmentsOperation: UseLalSegmentsOperation = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [archiveLalSegment] = useMutation(ArchiveLalSegmentDocument);
  const [pushLog] = useMutation(CreateLogDocument);

  const actions = useMemo(() => {
    const refetchQueries = ['GetLalSegmentsList', 'GetLalSegmentFeed', 'GetLalSegment'];

    return {
      [lalSegmentRowActionType.DELETE]: ({ id }: LalSegmentBase) =>
        archiveLalSegment({
          refetchQueries,
          variables: {
            id,
          },
          update: (cache, mutationResult) => {
            if (!mutationResult.data?.archiveLookalikeObjectAndSegments) {
              return;
            }

            cache.evict({ fieldName: 'lookalike_definition', id: 'ROOT_QUERY' });
            cache.gc();
          },
        }),
    };
  }, [archiveLalSegment]);

  const performSegmentAction = useCallback(
    async <T extends LalSegmentBase>(
      segments: T[],
      action: LalSegmentRowActionType
    ): Promise<SegmentOperationResult> => {
      const fulfilled: string[] = [];
      const rejected: string[] = [];

      const promises = segments.map(async segment => {
        try {
          await actions[action](segment);

          fulfilled.push(segment.id);
        } catch (_error) {
          rejected.push(segment.id);

          const location = window.location.href;
          await pushLog({
            variables: {
              email: currentUser?.email,
              location: location,
              msg: `Failed to archive lookalike segment${segments.length > 1 ? 's' : ''}`,
              name: currentUser?.displayName,
            },
          });
        }
      });

      await Promise.all(promises);

      if (segments.length === 1 && fulfilled.length) {
        const message = t('LAL Segment {name} successfully {finishedAction}.', {
          finishedAction: finishedActionLabel[action],
          name: segments[0].lookalike_name,
        });
        enqueueSnackbar(message, { variant: 'success' });
      } else if (fulfilled.length === segments.length) {
        const message = t('LAL Segments successfully {finishedAction}.', {
          finishedAction: finishedActionLabel[action],
        });
        enqueueSnackbar(message, { variant: 'success' });
      } else if (!fulfilled.length) {
        const message = t('Failed to {action} {segment}.', {
          action: action.toLowerCase(),
          segment: rejected.length === 1 ? 'segment' : 'segments',
        });

        enqueueSnackbar(message, {
          variant: 'error',
        });
      } else {
        const message = t('{archived} of {all} was successfully {finishedAction}.', {
          all: segments.length,
          archived: fulfilled.length,
          finishedAction: finishedActionLabel[action],
        });
        enqueueSnackbar(message, { variant: 'success' });
      }

      return { fulfilled, rejected };
    },
    [t, pushLog, actions, currentUser?.displayName, currentUser?.email]
  );

  const archive = useCallback(
    (segments: LalSegmentBase[]) => performSegmentAction(segments, lalSegmentRowActionType.DELETE),
    [performSegmentAction]
  );

  return { archive };
};
